import { ImagePaths } from '../../lib/imageUrls.js';

export const cyberWardensUplift = {

    id: 36,
    slug: 'cyber-wardens-uplift',
    ogImage: '/og/courses/cyber-wardens-uplift.jpg',
    courseCode: 'CYBER-WARDEN-001',
    featured: false,
    title: 'Cyber Wardens Uplift for Australian Small Business',
    subtitle: 'Step up from the free Cyber Wardens programme to practical incident response and Essential Eight Maturity Level One readiness',
    valueProposition: 'Take the foundations from the ASBFEO and CommBank Cyber Wardens programme and turn them into a working incident response capability, a defensible Notifiable Data Breaches position, an underwriting-ready insurance evidence kit, and a 90-day uplift plan to ACSC Essential Eight Maturity Level One.',
    duration: '4 hours',
    students: 'New',
    level: 'Foundational',
    image: ImagePaths.COURSE_2,
    shortDescription: 'CYBER-WARDEN-001 — small business cyber uplift covering incident response, Notifiable Data Breaches and Essential Eight ML1 for owners and office managers.',
    price: {
      aud: 399,
      currency: 'AUD',
      earlyBird: 299
    },
    stripeConfig: {
      productId: 'prod_Uarsp7fPwg7ARJ',
      priceId: 'price_1TikaJKu1nyc322Ut3cBJHa1'
    },

    snapshot: {
      whoItsFor: [
        'Small business owners who have completed (or want to complete) the free Cyber Wardens programme',
        'Office managers and admin staff acting as the de facto IT and security lead',
        'Bookkeepers, practice managers and operations leads holding customer or patient data',
        'Franchisees, trades and professional services firms preparing for cyber insurance renewal'
      ],
      accreditation: 'Aligned to the ACSC Small Business Cyber Security Guide and the ASD Essential Eight (path to Maturity Level One)'
    },

    logistics: {
      classSize: 'One half-day live session, or two 2-hour evening sessions (also available self-paced online with live Q&A)',
      requirements: [
        'A laptop or desktop and access to your business email and accounting tools',
        'Completion of, or willingness to complete, the free Cyber Wardens programme (cyberwardens.com.au)',
        'A nominated business owner or manager who can sign off the 90-day plan',
        'Basic familiarity with how your business stores customer and staff information'
      ]
    },

    overview: 'The Cyber Wardens programme run by the Council of Small Business Organisations Australia (COSBOA) with the Australian Small Business and Family Enterprise Ombudsman (ASBFEO) and CommBank gives small businesses solid awareness foundations. This uplift course is the next step. In one half-day, or two short evening sessions, owners and staff move from awareness to a working playbook: a documented call tree for the first hour of an incident, plain-English Notifiable Data Breaches mechanics under the Privacy Act 1988 and OAIC guidance, an underwriting-ready cyber insurance evidence kit, and a costed 90-day plan to reach ACSC Essential Eight Maturity Level One.',

    outcomes: [
      'Run a credible first hour of an incident using a documented call tree covering the owner, MSP, lawyer, insurer and bank',
      'Decide when a data breach is likely to be an eligible data breach under the Notifiable Data Breaches scheme and what to do in the first 30 days',
      'Draft a notification to affected individuals and to the Office of the Australian Information Commissioner using OAIC templates',
      'Assemble an underwriting evidence kit that maps to common cyber insurance proposal questions (MFA, backups, patching, training, incident response)',
      'Self-assess current Essential Eight posture against Maturity Level One and identify the three highest-impact gaps',
      'Build a costed 90-day uplift plan that a non-technical owner can sign off and a managed service provider can deliver against',
      'Brief staff, accountants and board members on the business case for the uplift in plain English',
      'Avoid the most common small business mistakes that void cyber insurance cover or trigger an OAIC investigation'
    ],

    audience: [
      'Cyber Wardens programme graduates wanting the next practical step',
      'Small business owners and operators (1 to 50 staff)',
      'Office managers, practice managers and bookkeepers',
      'Franchisees and multi-site operators preparing for insurance renewal'
    ],

    prerequisites: [
      'Awareness-level cyber knowledge (Cyber Wardens or equivalent)',
      'Familiarity with the business\'s day-to-day tools (email, accounting, file storage)',
      'No technical background required'
    ],

    modules: [
      {
        number: 1,
        title: 'Incident response readiness for small business: the call tree, the lawyer, the insurer',
        duration: '60 min',
        topics: [
          'The first hour: who calls who, in what order, and from which device',
          'Building a one-page call tree covering owner, MSP, lawyer, insurer, bank and key customers',
          'When to call your insurer before you call the IT person, and why',
          'Preserving evidence without making it worse: laptops, logs, mailbox rules',
          'Tabletop exercise: a ransomware note appears on the office PC at 4:45pm Friday'
        ]
      },
      {
        number: 2,
        title: 'Notifiable Data Breaches in plain English',
        duration: '60 min',
        topics: [
          'What the Privacy Act 1988 and the Notifiable Data Breaches scheme actually require of small business',
          'The "eligible data breach" test and the 30-day assessment window',
          'Using the OAIC Notifiable Data Breach form and statement template',
          'Common small business triggers: lost laptop, misdirected email, compromised mailbox, stolen client list',
          'What to say (and not say) to affected customers, staff and the media'
        ]
      },
      {
        number: 3,
        title: 'Practical cyber insurance underwriting evidence kit',
        duration: '60 min',
        topics: [
          'How Australian cyber insurance proposals are scored in 2026',
          'The eight evidence items most underwriters want to see (MFA coverage, backup test, patch cadence, EDR, training, IR plan, vendor list, prior incidents)',
          'Building a shared evidence folder that survives staff turnover',
          'Avoiding warranty and condition breaches that void cover at claim time',
          'Working with your broker: questions to ask before renewal'
        ]
      },
      {
        number: 4,
        title: '90-day uplift plan to ACSC Essential Eight Maturity Level One',
        duration: '60 min',
        topics: [
          'Mapping Essential Eight Maturity Level One to small business reality',
          'The eight controls in owner language: what "good enough" looks like for ML1',
          'Sequencing: what to do in days 1-30, 31-60 and 61-90',
          'Costing the plan: in-house vs MSP, and what to ask for in a quote',
          'Signing off the plan: owner accountability, review cadence and evidence capture'
        ]
      }
    ],

    format: {
      type: 'Half-day live session or two 2-hour evening sessions (also self-paced online)',
      includes: [
        '4 hours of focused training tailored to Australian small business',
        'One-page incident response call tree template',
        'OAIC Notifiable Data Breach decision flowchart and notification draft',
        'Cyber insurance underwriting evidence checklist',
        'Essential Eight Maturity Level One self-assessment workbook',
        '90-day uplift plan template (owner sign-off ready)',
        'Certificate of completion'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Run on demand as a half-day session for teams, or join the next public evening cohort'
    },

    faqs: [
      {
        question: 'Do I need to complete the free Cyber Wardens programme first?',
        answer: 'Strongly recommended but not required. The free Cyber Wardens programme run by COSBOA, ASBFEO and CommBank covers the awareness foundations. This course assumes that baseline and focuses on the practical next step: incident response, Notifiable Data Breaches, insurance evidence and a 90-day Essential Eight plan.'
      },
      {
        question: 'Is this course aligned to the ACSC Small Business Cyber Security Guide?',
        answer: 'Yes. The 90-day plan maps directly to the controls described in the Australian Cyber Security Centre Small Business Cyber Security Guide and to Essential Eight Maturity Level One as defined by the Australian Signals Directorate, so the work you do here is defensible to auditors and insurers.'
      },
      {
        question: 'Will this help us pass a cyber insurance renewal?',
        answer: 'It is designed for exactly that. Module three walks through the underwriting questions Australian insurers ask in 2026 and helps you assemble an evidence kit you can hand to your broker, reducing the chance of cover being declined or premiums spiking at renewal.'
      },
      {
        question: 'What if we have already had a data breach?',
        answer: 'The course covers the Notifiable Data Breaches scheme in plain English, including the 30-day assessment window and OAIC notification templates. If you are mid-incident, contact us directly before enrolling so we can point you to immediate response support.'
      },
      {
        question: 'Can we run this in-house for our team or franchise network?',
        answer: 'Yes. The half-day format works well for a single business, a franchise network or a member association. Group rates apply and the templates can be co-branded for member rollouts.'
      },
      {
        question: 'Is this suitable for sole traders and micro-businesses?',
        answer: 'Yes. The call tree, NDB workflow and 90-day plan are sized for businesses from one person up to around 50 staff. Sole traders skip the staff training section and focus on owner-operated controls.'
      }
    ],

    australianContext: true,
    complianceFrameworks: [
      'ACSC Small Business Cyber Security Guide',
      'ASD Essential Eight (path to Maturity Level One)',
      'OAIC Australian Privacy Principle 11 and the Notifiable Data Breaches scheme',
      'Privacy Act 1988 (Cth)'
    ]
};
